import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import App from "@/App";
import { installGreekAccentGuard } from "@/utils/greekAccentGuard";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
);

// Global Greek-accent guard — strips tonos marks from any uppercase-rendered text.
// Backstop on top of <html lang="el"> for cases where we manually call .toUpperCase().
installGreekAccentGuard();

// Register Service Worker for PWA
let updateRequested = false;
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      // 1. Force-delete every cache from prior SW versions (v1 cached /api/*).
      const CURRENT_SW_CACHE = 'lefteria-fc-v8';
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.filter(k => k.startsWith('lefteria-fc-') && k !== CURRENT_SW_CACHE).map(k => caches.delete(k)));
        // Defense in depth: also purge any /api/* entries that may have ended
        // up in the current cache from a buggy older SW.
        try {
          const c = await caches.open(CURRENT_SW_CACHE);
          const reqs = await c.keys();
          await Promise.all(reqs.filter(r => {
            try { return new URL(r.url).pathname.startsWith('/api/'); } catch { return false; }
          }).map(r => c.delete(r)));
        } catch {}
      }
      // 2. Unregister any previously-registered SW pointing at an old script.
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        if (reg.active && reg.scope !== `${window.location.origin}/` && /\/sw-(push|mobile)\.js$/.test(reg.active.scriptURL)) {
          await reg.unregister();
        }
      }
      // 3. Register the latest SW. Iter 91.12 — do NOT auto-reload; dispatch
      // a `lefteria-update-available` event so the mobile shell can render a
      // banner with an explicit "Ενημέρωση" button the user taps.
      const isPos = /^\/pos(?:\/|$)/.test(window.location.pathname);
      const reg = await navigator.serviceWorker.register(isPos ? '/pos/sw.js' : '/sw.js', { scope: isPos ? '/pos/' : '/' });
      window.__lefteriaSwRegistration = reg;
      const notifyAvailable = () => window.dispatchEvent(new CustomEvent('lefteria-update-available'));
      if (reg.waiting) notifyAvailable();
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (sw) sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            notifyAvailable();
          }
        });
      });
      // Poll for a new build every 30 min so silently-deployed updates surface.
      setInterval(() => { try { reg.update(); } catch {} }, 30 * 60 * 1000);
      // When the user confirms the update, this listener reloads exactly once
      // after the new SW takes control.
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // First activation (or an update accepted in another tab) must never
        // interrupt this tab's login, upload or unsaved form.
        if (!updateRequested || reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    } catch (e) { /* ignore */ }
  });
}

// Expose a helper for the update banner button.
window.__lefteriaApplyUpdate = () => {
  const reg = window.__lefteriaSwRegistration;
  if (reg && reg.waiting) {
    updateRequested = true;
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
  } else {
    window.location.reload();
  }
};
